import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  type ChartOptions,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);
import Navigation from "../app/components/NavigationSPA";
import type { AppDispatch } from "../app/redux/store";
import { selectIsAuthenticated } from "../app/redux/slices/authSlice";
import {
  fetchUserInfo,
  getRodentProbes,
  updateRodentProbeFields,
  getRodentProbeHistory,
  deleteRodentProbeHistory,
  deleteRodentProbeHistoryEntry,
  removeRodentProbe,
  selectCurrentTeam,
  selectRodentError,
  selectRodentLoading,
  selectRodentProbes,
  type RodentProbe,
  type HistoryEntry,
} from "../app/redux/slices/dataSlice";
import WifiIndicator from "../app/components/WifiIndicator";
import { logger } from "../app/utils/logger";
import { SERVER_REGION } from "../i18n";

// ── Types ────────────────────────────────────────────────────────────────────

type SortBy = "id" | "name" | "building" | "location";
type SortOrder = "asc" | "desc";
type ProbeTab = "general" | "specifications" | "functions";

interface FilterPrefs {
  searchText: string;
  selectedBuilding: string;
  selectedLocation: string;
  sortBy: SortBy;
  sortOrder: SortOrder;
}

const DEFAULT_PREFS: FilterPrefs = {
  searchText: "",
  selectedBuilding: "all",
  selectedLocation: "all",
  sortBy: "name",
  sortOrder: "asc",
};

const PREF_KEY = "rodentFilterPreferences";

// ── Utility helpers ───────────────────────────────────────────────────────────

function formatDateTime(dateString?: string | null): string {
  if (!dateString) return "None";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function convertWakeUpTime(wakeUpTime?: string | null): string {
  if (!wakeUpTime) return "Not set";
  try {
    let date: Date;
    if (wakeUpTime.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
      const [h, m] = wakeUpTime.split(":").map(Number);
      const now = new Date();
      date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0));
    } else {
      date = new Date(wakeUpTime);
    }
    if (isNaN(date.getTime())) return wakeUpTime;
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true, timeZoneName: "short" });
  } catch {
    return wakeUpTime;
  }
}

function formatConnectionWindow(wakeUpTime?: string | null, windowMinutes = 60): string {
  if (!wakeUpTime) return "Not set";
  try {
    const [h, m] = wakeUpTime.split(":").map(Number);
    const start = new Date();
    start.setUTCHours(h, m, 0, 0);
    const end = new Date(start.getTime() + windowMinutes * 60000);
    const fmt = (d: Date) =>
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${fmt(start)} – ${fmt(end)} (${windowMinutes} min)`;
  } catch {
    return wakeUpTime;
  }
}

function getBatteryPercent(batteryInfo?: string | null): number {
  if (!batteryInfo || batteryInfo === "0") return 0;
  const v = parseFloat(batteryInfo);
  if (isNaN(v)) return 0;
  return Math.max(0, Math.min(100, Math.round(((v - 3.0) / (4.2 - 3.0)) * 100)));
}

// 5-tier color scale matching the voltage table:
// 80-100% (4.0-4.2V) → Green    #4CAF50
// 60-80%  (3.8-4.0V) → Lt Green #8BC34A
// 40-60%  (3.56-3.8V)→ Amber    #FFC107
// 20-40%  (3.32-3.56V)→ Orange  #FF9800
// 0-20%   (3.0-3.32V)→ Red      #F44336
// 0% / null          → Gray     #CCCCCC
function getBatteryColor(pct: number): string {
  if (pct <= 0)  return "#CCCCCC";
  if (pct < 20)  return "#F44336";
  if (pct < 40)  return "#FF9800";
  if (pct < 60)  return "#FFC107";
  if (pct < 80)  return "#8BC34A";
  return "#4CAF50";
}

function getModeLabel(mode?: string | null): string {
  if (!mode || mode === "0") return "None Set";
  if (mode === "1") return "Wi-Fi";
  if (mode === "2") return "Offline";
  return "None Set";
}

function isOfflineMode(probe: { mode?: string | null; offlineMode?: boolean }): boolean {
  return probe.mode === "2" || !!probe.offlineMode;
}

// ── Battery Icon ──────────────────────────────────────────────────────────────

function BatteryIcon({ batteryInfo }: { batteryInfo?: string | null }) {
  const pct = getBatteryPercent(batteryInfo);
  const color = getBatteryColor(pct);
  // Inner fill width: max content area is 20px wide
  const fillWidth = pct <= 0 ? 0 : Math.max(2, Math.round((pct / 100) * 20));

  return (
    <div className="flex items-center" title={`${pct}% (${batteryInfo ?? "—"}V)`}>
      <svg width="30" height="16" viewBox="0 0 30 16" fill="none">
        {/* Battery body */}
        <rect x="1" y="2" width="23" height="12" rx="2" stroke="#888" strokeWidth="1.5" fill="white" />
        {/* Battery tip */}
        <rect x="24.5" y="5.5" width="3.5" height="5" rx="1" fill="#888" />
        {/* Fill level */}
        {fillWidth > 0 && (
          <rect x="2.5" y="3.5" width={fillWidth} height="9" rx="1.5" fill={color} />
        )}
      </svg>
    </div>
  );
}

// ── Confirm Dialog ─────────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmDanger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({
  isOpen, title, message, confirmLabel = "Confirm", confirmDanger = false, onConfirm, onCancel,
}: ConfirmDialogProps) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer">
            Cancel
          </button>
          <button onClick={onConfirm} className={`flex-1 py-2.5 rounded-xl text-sm font-medium text-white cursor-pointer ${confirmDanger ? "bg-red-500 hover:bg-red-600" : "bg-blue-500 hover:bg-blue-600"}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── History Modal ──────────────────────────────────────────────────────────────

interface HistoryModalProps {
  probe: RodentProbe;
  onClose: () => void;
  dispatch: AppDispatch;
  onRefreshList: () => void;
}

function HistoryModal({ probe, onClose, dispatch, onRefreshList }: HistoryModalProps) {
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState<{ history: HistoryEntry[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmEntryId, setConfirmEntryId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await dispatch(getRodentProbeHistory(probe.probeId)).unwrap();
        setHistoryData(result);
      } catch (err) {
        logger.error("HistoryModal load failed:", err);
        setError("Failed to load history.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dispatch, probe.probeId]);

  const handleDeleteEntry = async (entryId: string) => {
    setDeletingId(entryId);
    try {
      const result = await dispatch(deleteRodentProbeHistoryEntry({ probeId: probe.probeId, entryId })).unwrap();
      setHistoryData((prev) =>
        prev ? { history: prev.history.filter((e) => e._id !== entryId), total: prev.total - 1 } : prev
      );
      if (result.probeUpdated) onRefreshList();
    } catch (err) {
      logger.error("HistoryModal deleteEntry failed:", err);
      setError("Failed to delete entry.");
    } finally {
      setDeletingId(null);
      setConfirmEntryId(null);
    }
  };

  const handleClearAll = async () => {
    setConfirmClear(false);
    setLoading(true);
    try {
      await dispatch(updateRodentProbeFields({ probeId: probe.probeId, updates: { resetTimestamps: true, probeTriggered: false } })).unwrap();
      await dispatch(deleteRodentProbeHistory(probe.probeId)).unwrap();
      setHistoryData({ history: [], total: 0 });
      onRefreshList();
    } catch (err) {
      logger.error("HistoryModal clearAll failed:", err);
      setError("Failed to delete all history.");
    } finally {
      setLoading(false);
    }
  };

  const displayName = probe.probeName?.trim() || `Sensor ${probe.probeId}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col" style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="flex items-center px-5 py-4 border-b border-gray-100">
          <button onClick={onClose} className="mr-3 text-blue-500 hover:text-blue-700 cursor-pointer">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h3 className="text-base font-semibold text-gray-900">History – {displayName}</h3>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
          ) : !historyData || historyData.history.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No history available for this sensor.</div>
          ) : (
            <>
              <p className="text-sm text-gray-400 mb-4">Total Entries: <strong className="text-gray-700">{historyData.total}</strong></p>
              <div className="space-y-3">
                {historyData.history.map((entry, idx) => (
                  <div key={entry._id || idx} className="flex items-start gap-3 bg-gray-50 rounded-xl p-3">
                    <div className={`mt-1 flex-shrink-0 w-2.5 h-2.5 rounded-full ${entry.probeTriggered ? "bg-red-500" : "bg-green-500"}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${entry.probeTriggered ? "text-red-600" : "text-green-600"}`}>
                        {entry.probeTriggered ? "Motion Detected" : "No Motion Detected"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">Motion: {formatDateTime(entry.activityTimeStampString)}</p>
                      <p className="text-xs text-gray-500">Received: {formatDateTime(entry.timeDateString)}</p>
                    </div>
                    <button
                      onClick={() => setConfirmEntryId(entry._id)}
                      disabled={deletingId === entry._id}
                      className="flex-shrink-0 text-red-400 hover:text-red-600 disabled:opacity-40 cursor-pointer"
                    >
                      {deletingId === entry._id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-400"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && historyData && historyData.history.length > 0 && (
          <div className="px-5 py-4 border-t border-gray-100">
            <button
              onClick={() => setConfirmClear(true)}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 cursor-pointer"
            >
              Delete All Data
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog isOpen={confirmClear} title="Delete All Data?" message="This will permanently delete all history entries and reset timestamps. This action cannot be undone." confirmLabel="Delete All" confirmDanger onConfirm={handleClearAll} onCancel={() => setConfirmClear(false)} />
      <ConfirmDialog isOpen={!!confirmEntryId} title="Delete Entry?" message="This will permanently delete this history entry. This action cannot be undone." confirmLabel="Delete" confirmDanger onConfirm={() => confirmEntryId && handleDeleteEntry(confirmEntryId)} onCancel={() => setConfirmEntryId(null)} />
    </div>
  );
}

// ── Time Picker Modal ──────────────────────────────────────────────────────────

interface TimePickerProps {
  initialHour: string;
  initialMinute: string;
  initialPeriod: "AM" | "PM";
  onSave: (hour: string, minute: string, period: "AM" | "PM") => void;
  onCancel: () => void;
}

function TimePickerModal({ initialHour, initialMinute, initialPeriod, onSave, onCancel }: TimePickerProps) {
  const [hour, setHour] = useState(initialHour);
  const [minute, setMinute] = useState(initialMinute);
  const [period, setPeriod] = useState<"AM" | "PM">(initialPeriod);
  const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-5 text-center">Set Wake-Up Time</h3>
        <div className="flex gap-2 items-center justify-center mb-6">
          <select value={hour} onChange={(e) => setHour(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
            {hours.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
          <span className="text-gray-400 font-bold text-lg">:</span>
          <select value={minute} onChange={(e) => setMinute(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
            {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={period} onChange={(e) => setPeriod(e.target.value as "AM" | "PM")} className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer">Cancel</button>
          <button onClick={() => onSave(hour, minute, period)} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 cursor-pointer">Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Sensor Modal ──────────────────────────────────────────────────────────

interface EditModalProps {
  probe: RodentProbe;
  onClose: () => void;
  dispatch: AppDispatch;
  onSaved: () => void;
}

function EditSensorModal({ probe, onClose, dispatch, onSaved }: EditModalProps) {
  const [name, setName] = useState(probe.probeName || "");
  const [location, setLocation] = useState(probe.location || "");
  const [building, setBuilding] = useState(probe.building || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [localWakeUp, setLocalWakeUp] = useState(convertWakeUpTime(probe.wakeUpTime));
  const [pendingWakeUtc, setPendingWakeUtc] = useState<string | null>(null);

  const parseInitialTime = () => {
    const match = convertWakeUpTime(probe.wakeUpTime).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (match) return { hour: match[1].padStart(2, "0"), minute: match[2], period: match[3].toUpperCase() as "AM" | "PM" };
    return { hour: "08", minute: "00", period: "AM" as const };
  };
  const initial = parseInitialTime();
  const [pickerHour, setPickerHour] = useState(initial.hour);
  const [pickerMinute, setPickerMinute] = useState(initial.minute);
  const [pickerPeriod, setPickerPeriod] = useState<"AM" | "PM">(initial.period);

  const handleTimeSave = (h: string, min: string, p: "AM" | "PM") => {
    setPickerHour(h); setPickerMinute(min); setPickerPeriod(p);
    let hour24 = parseInt(h);
    if (p === "PM" && hour24 !== 12) hour24 += 12;
    else if (p === "AM" && hour24 === 12) hour24 = 0;
    setPendingWakeUtc(`${String(hour24).padStart(2, "0")}:${min}:00`);
    setLocalWakeUp(`${h}:${min} ${p}`);
    setShowTimePicker(false);
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const updates: Record<string, unknown> = { probeName: name, location, building };
      if (pendingWakeUtc !== null) updates.wakeUpTime = pendingWakeUtc;
      await dispatch(updateRodentProbeFields({ probeId: probe.probeId, updates })).unwrap();
      onSaved();
      onClose();
    } catch (err) {
      logger.error("EditSensorModal save failed:", err);
      setError("Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-gray-900">Edit Sensor</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>}

          <div className="space-y-4">
            {[
              { label: "Sensor Name", value: name, onChange: setName, placeholder: "Sensor name" },
              { label: "Location", value: location, onChange: setLocation, placeholder: "Location" },
              { label: "Building", value: building, onChange: setBuilding, placeholder: "Building" },
            ].map(({ label, value, onChange, placeholder }) => (
              <div key={label}>
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">{label}</label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={placeholder}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Wake-Up Time</label>
              <div className="flex items-center gap-3">
                <span className="flex-1 text-sm text-gray-700">{localWakeUp}</span>
                <button type="button" onClick={() => setShowTimePicker(true)} className="px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer border border-gray-200">
                  Set Time
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={onClose} disabled={saving} className="flex-1 py-3 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer disabled:opacity-50">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 py-3 rounded-xl text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>}
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
      {showTimePicker && (
        <TimePickerModal initialHour={pickerHour} initialMinute={pickerMinute} initialPeriod={pickerPeriod} onSave={handleTimeSave} onCancel={() => setShowTimePicker(false)} />
      )}
    </>
  );
}

// ── Filter/Sort Modal ──────────────────────────────────────────────────────────

interface FilterModalProps {
  prefs: FilterPrefs;
  allBuildings: string[];
  allLocations: string[];
  onApply: (prefs: FilterPrefs) => void;
  onClose: () => void;
}

function FilterSortModal({ prefs, allBuildings, allLocations, onApply, onClose }: FilterModalProps) {
  const [temp, setTemp] = useState<FilterPrefs>({ ...prefs });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-gray-900">Filter & Sort</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Search</label>
            <input type="text" value={temp.searchText} onChange={(e) => setTemp((p) => ({ ...p, searchText: e.target.value }))} placeholder="Search by name, ID, location, building..." className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Building</label>
            <select value={temp.selectedBuilding} onChange={(e) => setTemp((p) => ({ ...p, selectedBuilding: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
              <option value="all">All Buildings</option>
              {allBuildings.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Location</label>
            <select value={temp.selectedLocation} onChange={(e) => setTemp((p) => ({ ...p, selectedLocation: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
              <option value="all">All Locations</option>
              {allLocations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Sort By</label>
            <select value={temp.sortBy} onChange={(e) => setTemp((p) => ({ ...p, sortBy: e.target.value as SortBy }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer">
              <option value="id">ID</option>
              <option value="name">Name</option>
              <option value="building">Building</option>
              <option value="location">Location</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-2">
              {(["asc", "desc"] as const).map((ord) => (
                <button key={ord} onClick={() => setTemp((p) => ({ ...p, sortOrder: ord }))} className={`flex-1 py-2.5 rounded-xl text-sm font-medium cursor-pointer border ${temp.sortOrder === ord ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
                  {ord === "asc" ? "Ascending" : "Descending"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={() => setTemp({ ...DEFAULT_PREFS })} className="px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer border border-gray-200">Reset</button>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer">Cancel</button>
          <button onClick={() => { onApply(temp); onClose(); }} className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-blue-500 text-white hover:bg-blue-600 cursor-pointer">Apply</button>
        </div>
      </div>
    </div>
  );
}

// ── Probe Card ────────────────────────────────────────────────────────────────

interface ProbeCardProps {
  probe: RodentProbe;
  isExpanded: boolean;
  activeTab: ProbeTab;
  onToggle: () => void;
  onTabChange: (tab: ProbeTab) => void;
  onEdit: () => void;
  onHistory: () => void;
  onRemove: () => void;
}

function ProbeCard({ probe, isExpanded, activeTab, onToggle, onTabChange, onEdit, onHistory, onRemove }: ProbeCardProps) {
  const navigate = useNavigate();
  const motion = !!probe.probeTriggered;
  const offline = isOfflineMode(probe);
  const displayName = probe.probeName?.trim() || `ID: ${probe.probeId}`;
  const showId = !!probe.probeName?.trim();
  const battPct = getBatteryPercent(probe.batteryInfo);
  const [showOfflineInfo, setShowOfflineInfo] = useState(false);

  return (
    <div className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-colors ${isExpanded ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"}`}>
      {/* ── Offline Mode Info Modal ── */}
      {showOfflineInfo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowOfflineInfo(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl p-6 mx-4 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
                </svg>
              </div>
              <h3 className="text-base font-bold text-gray-900">Offline Mode</h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Offline Mode means that this sensor needs to be physically accessed to upload its data.
            </p>
            <button
              onClick={() => setShowOfflineInfo(false)}
              className="mt-5 w-full py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 cursor-pointer"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── Collapsed header ── */}
      <div className="px-4 pt-4 pb-3 cursor-pointer select-none hover:bg-gray-50 transition-colors" onClick={onToggle}>
        {/* Row 1: Name + icons + chevron */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-base font-bold text-gray-900 truncate">
              {probe.probeName?.trim() ? probe.probeName.trim() : `ID: ${probe.probeId}`}
            </span>
            {offline && (
              <span className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                (Offline Mode)
                <button
                  onClick={(e) => { e.stopPropagation(); setShowOfflineInfo(true); }}
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 text-gray-400 text-xs leading-none hover:bg-gray-100 cursor-pointer"
                >
                  i
                </button>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 ml-2 flex-shrink-0">
            {probe.wifiInfo && probe.wifiInfo !== "0" && (
              <WifiIndicator iconvalue={probe.wifiInfo} size={28} />
            )}
            <BatteryIcon batteryInfo={probe.batteryInfo} />
            {/* Chevron */}
            <svg className={`w-4 h-4 text-gray-400 ml-1 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        </div>


        {/* Row 3: Motion status text */}
        <p className={`text-sm font-semibold mt-1 ${motion ? "text-red-500" : "text-green-500"}`}>
          {motion ? "Motion Detected" : "No Motion Detected"}
        </p>

        {/* Row 4: Timestamps on one row */}
        <div className="flex gap-4 mt-0.5">
          <p className="text-xs text-gray-500">
            Motion Detected: {formatDateTime(probe.activityTimeStampString)}
          </p>
          <p className="text-xs text-gray-500">
            Data Received: {formatDateTime(probe.timeDateString)}
          </p>
        </div>
      </div>

      {/* ── Expanded tabs ── */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Tab bar — segmented style */}
          <div className="flex border-b border-gray-100 bg-gray-50">
            {(["general", "specifications", "functions"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => onTabChange(tab)}
                className={`flex-1 py-2.5 text-sm font-medium cursor-pointer transition-colors border-b-2 ${
                  activeTab === tab
                    ? "border-blue-500 text-blue-600 bg-white"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab === "general" ? "General" : tab === "specifications" ? "Specs" : "Functions"}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="px-4 py-4">
            {activeTab === "general" && (
              <div className="space-y-2.5">
                {[
                  ["Sensor Name", probe.probeName || "Not set"],
                  ["Location", probe.location || "Not set"],
                  ["Building", probe.building || "Not set"],
                  ["Wake Up Time", convertWakeUpTime(probe.wakeUpTime)],
                  ...(offline ? [["Connection Window", formatConnectionWindow(probe.wakeUpTime, probe.wakeUpWindow ? parseInt(probe.wakeUpWindow) : 60)]] : []),
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between items-start">
                    <span className="text-sm text-gray-400">{label}:</span>
                    <span className="text-sm text-gray-700 font-medium text-right ml-4 max-w-[60%]">{value}</span>
                  </div>
                ))}
                {SERVER_REGION !== 'CN' && (
                  <div className="pt-1">
                    {probe.latitude != null && probe.longitude != null ? (
                      <button
                        onClick={() => navigate(`/map?selectedProbeId=${probe.probeId}`)}
                        className="w-full py-2 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer transition-colors"
                      >
                        View on Map
                      </button>
                    ) : (
                      <p className="text-xs text-gray-400 text-center">Not placed on map</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "specifications" && (
              <div className="space-y-2.5">
                {[
                  ["Sensor ID", probe.probeId],
                  ["Mode", getModeLabel(probe.mode)],
                  ["Timezone of Sensor", probe.rodentLatestTimeZone || "—"],
                  ["Firmware Version", probe.firmwareVersion || "—"],
                  ["MAC Address", probe.macAddress || "—"],
                  ["WiFi Signal", probe.wifiInfo && probe.wifiInfo !== "0" ? probe.wifiInfo : "—"],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between items-start">
                    <span className="text-sm text-gray-400">{label}:</span>
                    <span className="text-sm text-gray-700 font-medium text-right ml-4 max-w-[60%] break-all">{value}</span>
                  </div>
                ))}
                {/* Battery Level — rendered separately to include color dot */}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Battery Level:</span>
                  <div className="flex items-center gap-2 ml-4">
                    {probe.batteryInfo && probe.batteryInfo !== "0" ? (
                      <>
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getBatteryColor(battPct) }}
                        />
                        <span className="text-sm text-gray-700 font-medium">
                          {probe.batteryInfo}V &nbsp;•&nbsp; {battPct}%
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-700 font-medium">—</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "functions" && (
              <div className="flex gap-2 pt-1">
                <button
                  onClick={onHistory}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold bg-blue-500 text-white hover:bg-blue-600 cursor-pointer transition-colors"
                >
                  View History
                </button>
                <button
                  onClick={onEdit}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold bg-blue-500 text-white hover:bg-blue-600 cursor-pointer transition-colors"
                >
                  Edit Sensor
                </button>
                <button
                  onClick={onRemove}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold bg-red-500 text-white hover:bg-red-600 cursor-pointer transition-colors"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Triggers By Month Chart ────────────────────────────────────────────────────

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

interface DayCount { day: number; count: number; }

function TriggersByMonthChart({ teamName }: { teamName: string }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-indexed
  const [data, setData] = useState<DayCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!teamName) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const token = localStorage.getItem("GrainGuardianToken");
    fetch("https://api.aivisionfood.com/api/rodentProbeAnalytics/triggersByDate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        teamsName: teamName,
        month: `${MONTH_NAMES[month - 1]} ${year}`,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        // Response shape: { days: [{ date: "2026-01-06", triggeredCount: 1, sensorIds: [] }] }
        const raw: Array<{ date: string; triggeredCount: number }> = json.days ?? [];
        const filled: DayCount[] = raw.map((entry) => ({
          day: new Date(entry.date + "T12:00:00").getDate(),
          count: entry.triggeredCount ?? 0,
        }));
        setData(filled);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError("Could not load chart data.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [teamName, year, month]);

  const goBack = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const goForward = () => {
    const nextIsInFuture = year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
    if (nextIsInFuture) return;
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };
  const isAtCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  const chartOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { title: (items) => `Day ${items[0].label}` } },
    },
    scales: {
      x: {
        ticks: { font: { size: 9 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { font: { size: 9 }, stepSize: 1, precision: 0 },
        grid: { color: "#f3f4f6" },
      },
    },
  };

  const chartData = {
    labels: data.map((d) => String(d.day)),
    datasets: [
      {
        label: "Detections",
        data: data.map((d) => d.count),
        backgroundColor: "#3b82f6",
        borderRadius: 3,
        barPercentage: 0.8,
      },
    ],
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">All Sensors Detections per Day</p>
        <div className="flex items-center gap-1">
          <button
            onClick={goBack}
            className="p-0.5 rounded hover:bg-gray-100 text-gray-500 cursor-pointer"
            title="Previous month"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-xs text-gray-600 font-medium w-14 text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button
            onClick={goForward}
            disabled={isAtCurrentMonth}
            className={`p-0.5 rounded text-gray-500 ${isAtCurrentMonth ? "opacity-30" : "hover:bg-gray-100 cursor-pointer"}`}
            title="Next month"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div style={{ height: 160 }}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-gray-400">{error}</p>
          </div>
        ) : (
          <Bar data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
}

// ── Sensor History Chart ───────────────────────────────────────────────────────

interface HistoryDayCount { day: number; count: number; }

function SensorHistoryChart({ probeId, probeName }: { probeId: string; probeName: string }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [allEntries, setAllEntries] = useState<Array<{ recordedAt: string; probeTriggered: boolean }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Fetch all history whenever the selected probe changes
  useEffect(() => {
    if (!probeId) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setAllEntries([]);

    const token = localStorage.getItem("GrainGuardianToken");
    fetch(`https://api.aivisionfood.com/api/rodentProbe/history/${encodeURIComponent(probeId)}?limit=1000`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const entries =
          json.history ??
          [...(json.recentHistory ?? []), ...(json.archivedHistory ?? [])] ??
          [];
        setAllEntries(entries);
        // Jump to the month of the most recent entry if available
        if (entries.length > 0 && entries[0].recordedAt) {
          const d = new Date(entries[0].recordedAt);
          setYear(d.getFullYear());
          setMonth(d.getMonth() + 1);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError("Could not load sensor history.");
        setLoading(false);
      });

    return () => controller.abort();
  }, [probeId]);

  // Aggregate entries for the selected month
  const data: HistoryDayCount[] = useMemo(() => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const counts = new Array<number>(daysInMonth).fill(0);
    allEntries.forEach((entry) => {
      if (!entry.recordedAt) return;
      const d = new Date(entry.recordedAt);
      if (d.getFullYear() === year && d.getMonth() + 1 === month) {
        counts[d.getDate() - 1]++;
      }
    });
    return counts.map((count, i) => ({ day: i + 1, count }));
  }, [allEntries, year, month]);

  const goBack = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const goForward = () => {
    const isAtCurrent = year === now.getFullYear() && month === now.getMonth() + 1;
    if (isAtCurrent) return;
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };
  const isAtCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  const chartOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { title: (items) => `Day ${items[0].label}` } },
    },
    scales: {
      x: {
        ticks: { font: { size: 9 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 10 },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { font: { size: 9 }, stepSize: 1, precision: 0 },
        grid: { color: "#f3f4f6" },
      },
    },
  };

  const chartData = {
    labels: data.map((d) => String(d.day)),
    datasets: [
      {
        label: "Events",
        data: data.map((d) => d.count),
        backgroundColor: "#22c55e",
        borderRadius: 3,
        barPercentage: 0.8,
      },
    ],
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Sensor Activity</p>
          <p className="text-xs text-gray-500 truncate mt-0.5">{probeName}</p>
        </div>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          <button onClick={goBack} className="p-0.5 rounded hover:bg-gray-100 text-gray-500 cursor-pointer" title="Previous month">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-xs text-gray-600 font-medium w-14 text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button
            onClick={goForward}
            disabled={isAtCurrentMonth}
            className={`p-0.5 rounded text-gray-500 ${isAtCurrentMonth ? "opacity-30" : "hover:bg-gray-100 cursor-pointer"}`}
            title="Next month"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div style={{ height: 160 }}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-green-500" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-gray-400">{error}</p>
          </div>
        ) : (
          <Bar data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
}

// ── Rodent Overview Panel ─────────────────────────────────────────────────────

function RodentOverviewPanel({
  probes,
  teamName,
  selectedProbeId,
}: {
  probes: RodentProbe[];
  teamName: string;
  selectedProbeId: string | null;
}) {
  const total = probes.length;
  const motionCount = probes.filter((p) => p.probeTriggered === true).length;
  const noMotionCount = probes.filter((p) => p.probeTriggered === false).length;
  const wifiCount = probes.filter((p) => p.mode === "1").length;
  const offlineCount = probes.filter((p) => isOfflineMode(p)).length;
  const lowBatteryCount = probes.filter((p) => getBatteryPercent(p.batteryInfo) < 20 && getBatteryPercent(p.batteryInfo) > 0).length;

  const StatRow = ({
    label,
    value,
    dotColor,
    icon,
  }: {
    label: string;
    value: number;
    dotColor?: string;
    icon?: React.ReactElement;
  }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-1.5 text-xs text-gray-600 min-w-0">
        {dotColor && (
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
        )}
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span className="truncate">{label}</span>
      </div>
      <span className="text-xs font-semibold text-blue-600 ml-1 flex-shrink-0">{value}</span>
    </div>
  );

  return (
    <div
      className="bg-white shadow rounded-lg p-5 flex flex-col overflow-y-auto flex-shrink-0"
      style={{ width: "30rem", height: "calc(100vh - 8rem)" }}
    >
      <h2 className="text-base font-bold text-gray-900 mb-3">Overview</h2>

      {/* Stats — 2-column grid */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Summary</p>
        <div className="grid grid-cols-2 grid-rows-3 grid-flow-col gap-x-3 gap-y-0">
          {/* Left column: Total Sensors, Motion Detected, No Motion */}
          <StatRow
            label="Total Sensors"
            value={total}
            icon={
              <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
          />
          <StatRow label="Motion Detected" value={motionCount} dotColor="#EF4444" />
          <StatRow label="No Motion" value={noMotionCount} dotColor="#22C55E" />
          {/* Right column: Wi-Fi Mode, Offline Mode, Low Battery */}
          <StatRow
            label="Wi-Fi Mode"
            value={wifiCount}
            icon={
              <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
            }
          />
          <StatRow
            label="Offline Mode"
            value={offlineCount}
            icon={
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M12 12h.01M3 3l18 18" />
              </svg>
            }
          />
          <StatRow label="Low Battery" value={lowBatteryCount} dotColor="#F44336" />
        </div>
      </div>

      {/* Team detections chart */}
      {teamName && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <TriggersByMonthChart teamName={teamName} />
        </div>
      )}

      {/* Selected sensor history chart — or placeholder */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        {selectedProbeId ? (() => {
          const probe = probes.find((p) => p.probeId === selectedProbeId);
          const name = probe?.probeName?.trim() || `ID: ${selectedProbeId}`;
          return <SensorHistoryChart key={selectedProbeId} probeId={selectedProbeId} probeName={name} />;
        })() : (
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Sensor Activity</p>
            <p className="text-xs text-gray-400 mb-2">No sensor selected</p>
            <div
              className="rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2"
              style={{ height: 160 }}
            >
              <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.5l4-4 4 4 4-6 4 4" />
              </svg>
              <p className="text-xs text-gray-400 text-center px-4">Select a sensor from the list to view its activity history</p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

// ── Rodent Probe List ─────────────────────────────────────────────────────────

interface RodentProbeListProps {
  probes: RodentProbe[];
  expandedProbes: Record<string, boolean>;
  activeTabs: Record<string, ProbeTab>;
  onToggle: (probeId: string) => void;
  onTabChange: (probeId: string, tab: ProbeTab) => void;
  onEdit: (probe: RodentProbe) => void;
  onHistory: (probe: RodentProbe) => void;
  onRemove: (probe: RodentProbe) => void;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  selectedItemRef: React.RefObject<HTMLDivElement | null>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

function RodentProbeList({
  probes,
  expandedProbes,
  activeTabs,
  onToggle,
  onTabChange,
  onEdit,
  onHistory,
  onRemove,
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  onPageChange,
  selectedItemRef,
  scrollContainerRef,
}: RodentProbeListProps) {
  if (probes.length === 0) return null;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Pagination controls */}
      {totalCount > itemsPerPage && (
        <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-3">
          <span className="text-sm text-gray-500">
            {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)}–
            {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-md text-sm font-medium ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
              }`}
            >
              Prev
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-md text-sm font-medium ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Scrollable list — matches Dashboard's overflow-y-auto area */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto space-y-3 pr-1"
        style={{ maxHeight: "calc(100vh - 16rem)" }}
      >
        {probes.map((probe) => (
          <div
            key={probe.probeId}
            ref={expandedProbes[probe.probeId] ? selectedItemRef : undefined}
          >
            <ProbeCard
              probe={probe}
              isExpanded={!!expandedProbes[probe.probeId]}
              activeTab={activeTabs[probe.probeId] || "general"}
              onToggle={() => onToggle(probe.probeId)}
              onTabChange={(tab) => onTabChange(probe.probeId, tab)}
              onEdit={() => onEdit(probe)}
              onHistory={() => onHistory(probe)}
              onRemove={() => onRemove(probe)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function RodentSensorsPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentTeam = useSelector(selectCurrentTeam);
  const rodentProbes = useSelector(selectRodentProbes);
  const rodentLoading = useSelector(selectRodentLoading);
  const rodentError = useSelector(selectRodentError);

  const [initializing, setInitializing] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [prefs, setPrefs] = useState<FilterPrefs>(() => {
    try {
      const saved = localStorage.getItem(PREF_KEY);
      return saved ? { ...DEFAULT_PREFS, ...JSON.parse(saved) } : DEFAULT_PREFS;
    } catch {
      return DEFAULT_PREFS;
    }
  });

  const [expandedProbes, setExpandedProbes] = useState<Record<string, boolean>>({});
  const [activeTabs, setActiveTabs] = useState<Record<string, ProbeTab>>({});
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [editingProbe, setEditingProbe] = useState<RodentProbe | null>(null);
  const [historyProbe, setHistoryProbe] = useState<RodentProbe | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<RodentProbe | null>(null);

  const teamName = currentTeam?.name;

  useEffect(() => {
    try { localStorage.setItem(PREF_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
  }, [prefs]);

  useEffect(() => {
    if (!isAuthenticated) { navigate("/login"); return; }
    const init = async () => {
      try { await dispatch(fetchUserInfo()).unwrap(); }
      catch (err) { logger.error("RodentSensorsPage init failed:", err); }
      finally { setInitializing(false); }
    };
    init();
  }, [dispatch, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !teamName) return;
    dispatch(getRodentProbes({ teamName }));
  }, [dispatch, isAuthenticated, teamName]);

  const handleRefresh = useCallback(async () => {
    if (!teamName) return;
    try { await dispatch(getRodentProbes({ teamName })).unwrap(); }
    catch (err) { logger.error("RodentSensorsPage refresh failed:", err); }
  }, [dispatch, teamName]);

  const allBuildings = useMemo(() => {
    if (!rodentProbes) return [];
    return [...new Set(rodentProbes.map((p) => p.building).filter((b): b is string => !!b?.trim()))].sort();
  }, [rodentProbes]);

  const allLocations = useMemo(() => {
    if (!rodentProbes) return [];
    return [...new Set(rodentProbes.map((p) => p.location).filter((l): l is string => !!l?.trim()))].sort();
  }, [rodentProbes]);

  const filteredProbes = useMemo(() => {
    if (!rodentProbes) return [];
    const lower = prefs.searchText.toLowerCase();
    let list = rodentProbes.filter((probe) => {
      const matchSearch = !prefs.searchText ||
        (probe.probeName || "").toLowerCase().includes(lower) ||
        (probe.probeId || "").toLowerCase().includes(lower) ||
        (probe.location || "").toLowerCase().includes(lower) ||
        (probe.building || "").toLowerCase().includes(lower);
      const matchBuilding = prefs.selectedBuilding === "all" || probe.building === prefs.selectedBuilding;
      const matchLocation = prefs.selectedLocation === "all" || probe.location === prefs.selectedLocation;
      return matchSearch && matchBuilding && matchLocation;
    });
    return [...list].sort((a, b) => {
      const getVal = (p: RodentProbe) =>
        prefs.sortBy === "id" ? p.probeId :
        prefs.sortBy === "name" ? (p.probeName || "") :
        prefs.sortBy === "building" ? (p.building || "") : (p.location || "");
      const cmp = getVal(a).localeCompare(getVal(b));
      return prefs.sortOrder === "asc" ? cmp : -cmp;
    });
  }, [rodentProbes, prefs]);

  const totalPages = Math.max(1, Math.ceil(filteredProbes.length / itemsPerPage));
  const paginatedProbes = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProbes.slice(start, start + itemsPerPage);
  }, [filteredProbes, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [prefs]);

  // Handle "View on List" navigation from the map page — declared AFTER setCurrentPage(1)
  // so it always runs last on mount and its setCurrentPage(page) call wins.
  useEffect(() => {
    const selectId = (location.state as { selectId?: string } | null)?.selectId;
    if (!selectId || !rodentProbes?.length) return;
    // Clear the state so navigating back doesn't re-trigger
    navigate(location.pathname, { replace: true, state: {} });
    const idx = filteredProbes.findIndex((p) => p.probeId === selectId);
    if (idx === -1) return;
    const page = Math.ceil((idx + 1) / itemsPerPage);
    shouldScrollRef.current = true;
    setCurrentPage(page);
    setExpandedProbes({ [selectId]: true });
    setActiveTabs((t) => ({ ...t, [selectId]: "general" }));
  }, [location.state, rodentProbes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shouldScrollRef.current) return;
    shouldScrollRef.current = false;
    const timer = setTimeout(() => {
      const container = scrollContainerRef.current;
      const item = selectedItemRef.current;
      if (container && item) {
        const containerRect = container.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        container.scrollTo({ top: container.scrollTop + (itemRect.top - containerRect.top), behavior: 'smooth' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [paginatedProbes]);

  const toggleCard = (probeId: string) => {
    setExpandedProbes((prev) => {
      const isCurrentlyOpen = !!prev[probeId];
      if (!isCurrentlyOpen && !activeTabs[probeId]) {
        setActiveTabs((t) => ({ ...t, [probeId]: "general" }));
      }
      // Accordion: close all others, toggle the clicked one
      return isCurrentlyOpen ? {} : { [probeId]: true };
    });
  };

  const handleRemoveConfirmed = async () => {
    if (!removeConfirm) return;
    const id = removeConfirm.probeId;
    setRemoveConfirm(null);
    try {
      await dispatch(removeRodentProbe(id)).unwrap();
      await handleRefresh();
    } catch (err) {
      logger.error("Remove sensor failed:", err);
    }
  };

  const hasActiveFilters =
    prefs.searchText !== "" ||
    prefs.selectedBuilding !== "all" ||
    prefs.selectedLocation !== "all" ||
    prefs.sortBy !== DEFAULT_PREFS.sortBy ||
    prefs.sortOrder !== DEFAULT_PREFS.sortOrder;

  const isLoading = initializing || rodentLoading;

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />

      <main className="py-6 px-6">
        <div className="flex gap-5 items-start mx-auto" style={{ maxWidth: "88rem" }}>
        {/* ── Overview panel ── */}
        {rodentProbes && rodentProbes.length > 0 && (
          <RodentOverviewPanel
            probes={rodentProbes}
            teamName={teamName ?? ""}
            selectedProbeId={Object.keys(expandedProbes).find((id) => expandedProbes[id]) ?? null}
          />
        )}

        {/* ── Sensor list ── */}
        <div className="flex-1 min-w-0">
          {/* ── Fixed-height card — matches Dashboard's right column ── */}
          <div
            className="bg-white shadow rounded-lg p-6 flex flex-col"
            style={{ height: "calc(100vh - 8rem)" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Total Rodent Sensors: {rodentProbes?.length ?? 0}
              </h2>
              <div className="flex items-center gap-2">
                {/* Filter button */}
                <button
                  type="button"
                  onClick={() => setShowFilterModal(true)}
                  className={`p-2 rounded-full cursor-pointer transition-colors ${
                    hasActiveFilters ? "text-blue-500 hover:text-blue-600" : "text-gray-400 hover:text-gray-600"
                  }`}
                  title="Filter & Sort"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </button>
                {/* Refresh button */}
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={!teamName || isLoading}
                  className="p-2 rounded-full text-blue-500 hover:text-blue-700 disabled:text-gray-300 cursor-pointer transition-colors"
                  title="Refresh"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                  ) : (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Search bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search sensors..."
                value={prefs.searchText}
                onChange={(e) => setPrefs((p) => ({ ...p, searchText: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Body */}
            {isLoading ? (
              <div className="flex justify-center items-center flex-grow">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : rodentError ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                <strong>Error:</strong>{" "}
                {typeof rodentError === "string" ? rodentError : JSON.stringify(rodentError)}
              </div>
            ) : filteredProbes.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-grow text-center">
                <p className="text-gray-600 font-medium">
                  {!rodentProbes || rodentProbes.length === 0
                    ? "No rodent sensors for this team."
                    : "No results match your filters."}
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  {!rodentProbes || rodentProbes.length === 0
                    ? "If you recently assigned sensors, try refreshing."
                    : "Try adjusting your filter settings."}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={() => setPrefs(DEFAULT_PREFS)}
                    className="mt-3 px-4 py-2 rounded-md text-sm bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <RodentProbeList
                probes={paginatedProbes}
                expandedProbes={expandedProbes}
                activeTabs={activeTabs}
                onToggle={toggleCard}
                onTabChange={(probeId, tab) => setActiveTabs((prev) => ({ ...prev, [probeId]: tab }))}
                onEdit={setEditingProbe}
                onHistory={setHistoryProbe}
                onRemove={setRemoveConfirm}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={filteredProbes.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                selectedItemRef={selectedItemRef}
                scrollContainerRef={scrollContainerRef}
              />
            )}
          </div>
        </div>
        </div>
      </main>

      {showFilterModal && (
        <FilterSortModal prefs={prefs} allBuildings={allBuildings} allLocations={allLocations} onApply={setPrefs} onClose={() => setShowFilterModal(false)} />
      )}

      {editingProbe && (
        <EditSensorModal probe={editingProbe} onClose={() => setEditingProbe(null)} dispatch={dispatch} onSaved={handleRefresh} />
      )}

      {historyProbe && (
        <HistoryModal probe={historyProbe} onClose={() => setHistoryProbe(null)} dispatch={dispatch} onRefreshList={handleRefresh} />
      )}

      <ConfirmDialog
        isOpen={!!removeConfirm}
        title="Remove Sensor?"
        message={`Are you sure you want to remove "${removeConfirm?.probeName?.trim() || removeConfirm?.probeId || "this sensor"}" from your account? This action cannot be undone.`}
        confirmLabel="Remove"
        confirmDanger
        onConfirm={handleRemoveConfirmed}
        onCancel={() => setRemoveConfirm(null)}
      />
    </div>
  );
}
